import React, { useState } from 'react';
import { 
  X, User, GraduationCap, Stethoscope, Sparkles, CheckCircle2, 
  BarChart2, Printer, ShieldCheck, Activity, Award, AlertCircle
} from 'lucide-react';
import { DemoStudent } from '../data/demoStudents';

const ALL_30_MODULE_META = [
  { key: 'aac', label: '01. AAC Speech Synthesizer', category: 'Assistive Communication' },
  { key: 'speech-therapy', label: '02. Speech & Articulation', category: 'Clinical Therapy' },
  { key: 'autism', label: '03. Autism & ABA Behavior', category: 'Behavioral Health' },
  { key: 'sign-language', label: '04. Sign Language & Gestures', category: 'Visual / Hearing' },
  { key: 'braille', label: '05. Braille & Tactile Learning', category: 'Visual Impairment' },
  { key: 'student', label: '06. Student & IEP Progress', category: 'Special Education' },
  { key: 'therapist', label: '07. Therapist Workspace & SOAP', category: 'Clinical Operations' },
  { key: 'teacher', label: '08. Teacher & Classroom Lesson', category: 'School Education' },
  { key: 'assessment', label: '09. Diagnostic Assessment', category: 'Evaluations' },
  { key: 'school-erp', label: '10. Special School ERP', category: 'Administration' },
  { key: 'hospital', label: '11. Rehabilitation Hospital', category: 'Inpatient Care' },
  { key: 'billing', label: '12. NDIS & Insurance Billing', category: 'Finance' },
  { key: 'communication', label: '13. Parent Communication', category: 'Family Engagement' },
  { key: 'inventory', label: '14. Assistive Device Inventory', category: 'Resources' },
  { key: 'analytics', label: '15. Executive Analytics BI', category: 'Reporting' },
  { key: 'ai', label: '16. AI Copilot Summarizer', category: 'Intelligence' },
  { key: 'sensory-room', label: '17. Sensory Room Controller', category: 'Environmental' },
  { key: 'tele-rehab', label: '18. Tele-Rehabilitation Video', category: 'Tele-Health' },
  { key: 'early-intervention', label: '19. Early Intervention Milestone', category: 'Pediatrics' },
  { key: 'vocational-living', label: '20. Vocational & Life Skills', category: 'Adult Rehab' },
  { key: 'hydrotherapy', label: '21. Hydrotherapy Aquatic Rehab', category: 'Physical Rehab' },
  { key: 'music-therapy', label: '22. Music Auditory Stimulation', category: 'Neuro-Rehab' },
  { key: 'animal-therapy', label: '23. Animal-Assisted Therapy', category: 'Emotional' },
  { key: 'prosthetics', label: '24. Bionic Prosthetics & Fitting', category: 'Bionics' },
  { key: 'vbmapp-assessment', label: '25. VB-MAPP Assessment Protocol', category: 'Behavioral Health' },
  { key: 'core-vocab-ai', label: '26. Motor Core Vocab & AI', category: 'AAC AI' },
  { key: 'fim-weefim', label: '27. FIM Functional Scoring', category: 'Clinical Rehab' },
  { key: 'evv-gps-tracker', label: '28. EVV Visit Verification & GPS', category: 'Compliance' },
  { key: 'gamified-neuro', label: '29. WebGL Biofeedback Neuro-Rehab', category: 'Gamified Rehab' },
  { key: 'transition-matrix', label: '30. Secondary Transition Matrix', category: 'Post-Secondary' }
];

interface StudentClinicalAnalysisModalProps {
  student: DemoStudent | null;
  isOpen: boolean;
  onClose: () => void;
  onSelectModule?: (moduleKey: string) => void;
}

export const StudentClinicalAnalysisModal: React.FC<StudentClinicalAnalysisModalProps> = ({
  student,
  isOpen,
  onClose,
  onSelectModule
}) => {
  const [exportedMsg, setExportedMsg] = useState('');

  if (!isOpen || !student) return null;

  const handleExportPDF = () => {
    setExportedMsg(`Clinical Analysis Report for ${student.name} exported as PDF!`);
    setTimeout(() => setExportedMsg(''), 4000);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-5xl max-h-[90vh] overflow-y-auto relative flex flex-col">
        
        {/* Modal Header */}
        <div className="bg-gradient-to-r from-blue-900 via-indigo-900 to-slate-900 p-6 text-white sticky top-0 z-20 flex justify-between items-start border-b border-blue-500/30">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 bg-emerald-500/30 text-emerald-200 border border-emerald-400/40 text-[10px] font-extrabold rounded-full uppercase tracking-wider">
                Individual Clinical Analysis & 30-Module Mastery
              </span>
              <span className="text-xs font-mono font-bold text-blue-200">{student.rollId}</span>
            </div>
            <h2 className="text-2xl md:text-3xl font-black">{student.name}</h2>
            <p className="text-xs text-blue-200">
              Grade: <span className="text-white font-bold">{student.grade}</span> • Disability: <span className="text-amber-300 font-bold">{student.disabilityCategory}</span> • Therapist: <span className="text-white font-bold">{student.therapist}</span>
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleExportPDF}
              className="px-3.5 py-2 bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-xs rounded-xl shadow transition flex items-center gap-1.5"
            >
              <Printer className="w-4 h-4" />
              <span>Export PDF Report</span>
            </button>
            <button
              onClick={onClose}
              className="p-2 text-blue-200 hover:text-white rounded-full hover:bg-white/10 transition"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {exportedMsg && (
          <div className="p-3 bg-emerald-50 border-b border-emerald-200 text-emerald-900 text-xs font-bold flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            {exportedMsg}
          </div>
        )}

        {/* Modal Body Content */}
        <div className="p-6 space-y-6 flex-1">
          
          {/* Key Metrics Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            
            {/* Overall Mastery Gauge */}
            <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-2xl flex items-center gap-4">
              <div className="w-14 h-14 bg-blue-600 text-white rounded-2xl flex items-center justify-center font-black text-xl font-mono shadow-md">
                {student.overallMasteryPercent}%
              </div>
              <div>
                <div className="text-xs font-extrabold text-blue-900">Overall IEP Mastery Index</div>
                <div className="text-[11px] text-blue-700">Calculated across 30 modules</div>
              </div>
            </div>

            {/* Active IEP Goal Target */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-1">
              <div className="text-xs font-bold text-slate-500">Active SMART IEP Target</div>
              <div className="text-xs font-bold text-slate-900 line-clamp-2">{student.iepGoal}</div>
            </div>

            {/* Recent Assessment Score */}
            <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-1">
              <div className="text-xs font-bold text-slate-500">Latest Assessment Score</div>
              <div className="text-xs font-extrabold text-emerald-700">{student.recentScore}</div>
            </div>

          </div>

          {/* AI Clinical Recommendations Box */}
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-2xl space-y-2">
            <div className="flex items-center gap-2 font-bold text-amber-900 text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-amber-600" />
              AI Clinical Recommendation for {student.name}
            </div>
            <p className="text-xs text-slate-700 font-medium leading-relaxed">
              {student.aiRecommendation}
            </p>
          </div>

          {/* Clinical Summary */}
          <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-1 text-xs">
            <div className="font-bold text-slate-900">Clinical Overview & Progress History:</div>
            <p className="text-slate-600">{student.clinicalSummary}</p>
          </div>

          {/* ALL 30 MODULES CLINICAL MASTERY GRID */}
          <div className="space-y-3">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
                <BarChart2 className="w-5 h-5 text-blue-600" />
                30-Module Mastery Breakdown for {student.name}
              </h3>
              <span className="text-xs font-bold text-slate-500">Click any module to launch</span>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {ALL_30_MODULE_META.map((mod) => {
                const mastery = student.moduleMasteryMap[mod.key] || {
                  status: 'NOT_ASSESSED',
                  scorePercent: 0,
                  lastAssessedDate: 'N/A',
                  notes: 'Module not assessed yet'
                };

                return (
                  <div 
                    key={mod.key}
                    onClick={() => {
                      if (onSelectModule) {
                        onSelectModule(mod.key);
                        onClose();
                      }
                    }}
                    className="p-3.5 bg-white border border-slate-200 rounded-2xl shadow-sm hover:shadow-md hover:border-blue-300 cursor-pointer transition space-y-2 group"
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-slate-900 group-hover:text-blue-600 transition truncate pr-1">
                        {mod.label}
                      </span>
                      
                      <span className={`px-2 py-0.5 rounded text-[9px] font-extrabold shrink-0 ${
                        mastery.status === 'MASTERED' ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' :
                        mastery.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800 border border-blue-300' :
                        mastery.status === 'NEEDS_ATTENTION' ? 'bg-amber-100 text-amber-800 border border-amber-300' :
                        'bg-slate-100 text-slate-500'
                      }`}>
                        {mastery.status.replace('_', ' ')}
                      </span>
                    </div>

                    {/* Score Bar */}
                    <div className="space-y-1">
                      <div className="flex justify-between text-[10px] font-bold text-slate-500">
                        <span>Mastery Score</span>
                        <span className="font-mono text-slate-800">{mastery.scorePercent}%</span>
                      </div>
                      <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
                        <div 
                          className={`h-full transition-all ${
                            mastery.scorePercent >= 85 ? 'bg-emerald-500' :
                            mastery.scorePercent >= 70 ? 'bg-blue-500' :
                            mastery.scorePercent > 0 ? 'bg-amber-500' : 'bg-slate-300'
                          }`} 
                          style={{ width: `${mastery.scorePercent}%` }}
                        ></div>
                      </div>
                    </div>

                    <div className="text-[10px] text-slate-500 truncate pt-0.5 border-t border-slate-100">
                      <span className="font-bold text-slate-700">Note:</span> {mastery.notes}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

        </div>

      </div>
    </div>
  );
};
